import { useFormContext, get } from 'react-hook-form'
import type { FieldPath, FieldValues } from 'react-hook-form'

interface SelectOption {
  value: string
  label: string
}

interface SelectFieldProps<TFieldValues extends FieldValues> {
  name: FieldPath<TFieldValues>
  label: string
  options: ReadonlyArray<SelectOption>
  placeholder?: string
}

/**
 * Bound `<select>` wired through `react-hook-form`'s `useFormContext`.
 * The empty-string option is treated as "no selection" by the schema's
 * `emptyToUndefined` preprocessor.
 *
 * @returns JSX.Element The labeled select with inline error rendering
 */
export function SelectField<TFieldValues extends FieldValues>({
  name,
  label,
  options,
  placeholder = 'Select…',
}: SelectFieldProps<TFieldValues>): JSX.Element {
  const {
    register,
    formState: { errors },
  } = useFormContext<TFieldValues>()
  const error = get(errors, name) as { message?: string } | undefined
  const errorId = error ? `${name}-error` : undefined

  return (
    <div className="form-group">
      <label htmlFor={name}>{label}</label>
      <select
        id={name}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={errorId}
        {...register(name)}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error?.message && (
        <p id={errorId} className="field-error" role="alert">
          {String(error.message)}
        </p>
      )}
    </div>
  )
}
